/**
 * HTTP Implementation of Public Trophy Room Gateway
 *
 * Handles fetching public Trophy Room data via REST API
 * No authentication required (public endpoint)
 */

import type {
  IPublicTrophyRoomGateway,
  PublicTrophyRoomData,
} from "@domain/ports/IPublicTrophyRoomGateway";

export class HttpPublicTrophyRoomGateway implements IPublicTrophyRoomGateway {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    path: string,
    options: RequestInit = {}
  ): Promise<T> {
    const headers: HeadersInit = {
      "Content-Type": "application/json",
      ...options.headers,
    };

    console.log("[HttpPublicTrophyRoomGateway] Making request:", {
      path,
      method: options.method || "GET",
    });

    const response = await fetch(`${this.baseUrl}${path}`, {
      ...options,
      headers,
    });

    console.log("[HttpPublicTrophyRoomGateway] Response status:", {
      path,
      status: response.status,
      ok: response.ok,
      statusText: response.statusText,
    });

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ message: "Unknown error" }));
      console.error("[HttpPublicTrophyRoomGateway] Request failed:", error);

      // Handle specific error types
      if (response.status === 404) {
        throw new Error("UserNotFound");
      }

      // Extract error message from Zod validation format: { errors: [{ message: "..." }] }
      const errorMessage = error.errors?.[0]?.message || error.error || error.message || `HTTP error ${response.status}`;
      throw new Error(errorMessage);
    }

    const jsonData = await response.json();
    console.log("[HttpPublicTrophyRoomGateway] Parsed JSON:", {
      path,
      hasUser: "user" in jsonData,
      hasSocialLinks: "socialLinks" in jsonData,
      hasWallets: "wallets" in jsonData,
      hasItems: "items" in jsonData,
      socialLinksLength: jsonData.socialLinks?.length,
      walletsLength: jsonData.wallets?.length,
      itemsLength: jsonData.items?.length,
    });

    return jsonData;
  }

  async getPublicTrophyRoom(userId: string): Promise<PublicTrophyRoomData> {
    console.log("[HttpPublicTrophyRoomGateway] Fetching public Trophy Room for user:", userId);

    try {
      // API returns Trophy Room data directly (flat structure)
      const response = await this.request<PublicTrophyRoomData>(
        `/api/v1/viewer/trophy-room/${userId}`
      );

      console.log("[HttpPublicTrophyRoomGateway] Trophy Room fetched successfully:", {
        userId: response.user.userId,
        username: response.user.username,
        publicItemCount: response.user.publicItemCount,
        totalItems: response.items.length,
        socialLinksCount: response.socialLinks.length,
        walletsCount: response.wallets.length,
      });

      return response;
    } catch (error) {
      console.error("[HttpPublicTrophyRoomGateway] Failed to fetch Trophy Room:", error);

      // Re-throw with more context if it's a generic error
      if (error instanceof Error) {
        if (error.message === "Failed to fetch" || error.message.includes("network")) {
          throw new Error("NetworkError");
        }
        throw error;
      }

      throw new Error("UnknownError");
    }
  }
}
